import { useState, useCallback, useRef } from 'react'
import type { Editor } from '@tiptap/core'
import type { NotebookNote } from '../../shared/types/notebook'
import type { SlashCommand } from '../../shared/types/chat'
import { searchNotes } from './notebookTree'

export type MenuPhase = 'closed' | 'command_picker' | 'note_search'

export interface MenuCoords {
  top: number
  bottom: number
  left: number
}

const SLASH_RE = /(^|[\s])\/([^\s/]*)$/
const LINK_SHORTCUT_RE = /(^|[\s])\/link\s+(.*)$/
const LINK_RE = /\/link\s*(.*)$/

interface UseTiptapSlashMenuOpts {
  editor: Editor | null
  slashCommands: SlashCommand[]
  allNotesRef: React.RefObject<NotebookNote[]>
  onExecuteCommand?: (commandId: string) => void
}

/** Get text before cursor in the current Tiptap editor state */
function getTextBeforeCursor(editor: Editor): string {
  const { $from } = editor.state.selection
  // Get text from start of current text block to cursor
  return $from.parent.textBetween(0, $from.parentOffset, undefined, '\ufffc')
}

/** Remove the slash trigger text from the editor */
function removeSlashText(editor: Editor): void {
  const textBefore = getTextBeforeCursor(editor)
  const match = textBefore.match(/(^|[\s])\/[^\n]*$/)
  if (!match) return

  const { $from } = editor.state.selection
  const blockStart = $from.start()
  const deleteFrom = blockStart + textBefore.length - match[0].length + (match[1]?.length ?? 0)
  const deleteTo = blockStart + $from.parentOffset

  editor.chain().focus().deleteRange({ from: deleteFrom, to: deleteTo }).run()
}

export function useTiptapSlashMenu({
  editor,
  slashCommands,
  allNotesRef,
  onExecuteCommand,
}: UseTiptapSlashMenuOpts) {
  const [menuPhase, _setMenuPhase] = useState<MenuPhase>('closed')
  const menuPhaseRef = useRef<MenuPhase>('closed')
  const [items, setItemsState] = useState<Array<SlashCommand | NotebookNote>>([])
  const itemsRef = useRef<Array<SlashCommand | NotebookNote>>([])
  const [selectedIdx, setSelectedIdxState] = useState(0)
  const selectedIdxRef = useRef(0)
  const [menuCoords, setMenuCoords] = useState<MenuCoords | null>(null)

  const setMenuPhase = useCallback((p: MenuPhase) => {
    menuPhaseRef.current = p
    _setMenuPhase(p)
  }, [])
  const setItems = useCallback((a: Array<SlashCommand | NotebookNote>) => {
    itemsRef.current = a
    setItemsState(a)
  }, [])
  const setSelectedIdx = useCallback((v: number | ((p: number) => number)) => {
    const next = typeof v === 'function' ? v(selectedIdxRef.current) : v
    selectedIdxRef.current = next
    setSelectedIdxState(next)
  }, [])

  const dismiss = useCallback(() => {
    setMenuPhase('closed')
    setItems([])
    setSelectedIdx(0)
    setMenuCoords(null)
  }, [setMenuPhase, setItems, setSelectedIdx])

  /** Compute caret screen coordinates (top/bottom/left) for menu positioning */
  const computeCoords = useCallback((): MenuCoords | null => {
    if (!editor) return null
    try {
      const { $from } = editor.state.selection
      const c = editor.view.coordsAtPos($from.pos)
      return { top: c.top, bottom: c.bottom, left: c.left }
    } catch {
      return null
    }
  }, [editor])

  /* ── Slash detection (called on every editor update) ── */

  const checkSlash = useCallback(() => {
    if (!editor) {
      if (menuPhaseRef.current !== 'closed') dismiss()
      return
    }

    const { selection } = editor.state
    if (!selection.empty) {
      if (menuPhaseRef.current !== 'closed') dismiss()
      return
    }

    const textBefore = getTextBeforeCursor(editor)

    if (menuPhaseRef.current === 'note_search') {
      const lm = textBefore.match(LINK_RE)
      if (lm) {
        const q = (lm[1] ?? '').trim()
        const notes = allNotesRef.current!
        setItems(q ? searchNotes(notes, q) : notes.slice(0, 10))
        setSelectedIdx(0)
        setMenuCoords(computeCoords())
      } else {
        dismiss()
      }
      return
    }

    const sm = textBefore.match(SLASH_RE)
    if (!sm) {
      if (menuPhaseRef.current !== 'closed') dismiss()
      return
    }

    const dl = textBefore.match(LINK_SHORTCUT_RE)
    if (dl) {
      const q = (dl[2] ?? '').trim()
      const notes = allNotesRef.current!
      setMenuPhase('note_search')
      setItems(q ? searchNotes(notes, q) : notes.slice(0, 10))
      setSelectedIdx(0)
      setMenuCoords(computeCoords())
      return
    }

    const partial = (sm[2] ?? '').toLowerCase()
    const filtered = slashCommands.filter(
      c => c.id.startsWith(partial) || c.label.toLowerCase().includes(partial),
    )
    if (filtered.length > 0) {
      setMenuPhase('command_picker')
      setItems(filtered)
      setSelectedIdx(0)
      setMenuCoords(computeCoords())
    } else if (menuPhaseRef.current !== 'closed') {
      dismiss()
    }
  }, [editor, slashCommands, allNotesRef, setMenuPhase, setItems, setSelectedIdx, dismiss, computeCoords])

  /* ── Menu actions ── */

  const handleSelectCommand = useCallback((cmd: SlashCommand) => {
    if (!editor) return
    if (cmd.id === 'link') {
      // Replace the existing slash trigger with "/link " so continued typing
      // matches LINK_RE and filters the notes list.
      removeSlashText(editor)
      editor.chain().focus().insertContent('/link ').run()
      setMenuPhase('note_search')
      setItems(allNotesRef.current!.slice(0, 10))
      setSelectedIdx(0)
      setMenuCoords(computeCoords())
    } else {
      removeSlashText(editor)
      dismiss()
      onExecuteCommand?.(cmd.id)
    }
  }, [editor, allNotesRef, dismiss, onExecuteCommand, setMenuPhase, setItems, setSelectedIdx, computeCoords])

  const handleSelectNote = useCallback(
    (note: NotebookNote) => {
      if (!editor) return
      removeSlashText(editor)
      editor.commands.insertWikiLink(note.id)
      dismiss()
    },
    [editor, dismiss],
  )

  /* ── Keyboard handler — returns true if consumed ── */

  const handleMenuKeyDown = useCallback(
    (event: KeyboardEvent): boolean => {
      if (menuPhaseRef.current === 'closed') return false

      if (event.key === 'Escape') { event.preventDefault(); dismiss(); return true }
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setSelectedIdx(p => Math.min(p + 1, itemsRef.current.length - 1))
        return true
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setSelectedIdx(p => Math.max(p - 1, 0))
        return true
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        const idx = selectedIdxRef.current
        if (menuPhaseRef.current === 'command_picker') {
          const cmd = itemsRef.current[idx] as SlashCommand
          if (cmd) handleSelectCommand(cmd)
        } else if (menuPhaseRef.current === 'note_search') {
          const n = itemsRef.current[idx] as NotebookNote
          if (n) handleSelectNote(n)
        }
        return true
      }
      return false
    },
    [dismiss, setSelectedIdx, handleSelectCommand, handleSelectNote],
  )

  return {
    menuPhase,
    items,
    selectedIdx,
    menuCoords,
    checkSlash,
    dismiss,
    handleSelectCommand,
    handleSelectNote,
    handleMenuKeyDown,
  }
}
